import { ChangeDetectionStrategy, Component, computed, effect } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { OiqFormInputComponent } from '@oiq-ui-sdk/form';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import { CollectionFilterFormatPayloadList, CollectionFilterFromQueryList } from './filter.utils';

@Component({
  selector: 'collection-filter-ids',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let _definition = definition();
    @if (_definition) {
      <oiq-form-input
        type="text"
        [placeholder]="_definition.placeholder || _definition.label"
        [formControl]="internalControl"
        [canClear]="_definition.ids.canClear"
        [prefixIcon]="_definition.ids.prefixIcon"
        [suffixIcon]="_definition.ids.suffixIcon"
        [enterSubmit]="false"
        (event)="onValueChange($event)"
        [errorMap]="{
          emptyIds: 'There are empty IDs defined',
          invalidIds: 'Please type only numbers',
        }"
        [class.mb-2]="
          internalControl.invalid && (internalControl.dirty || internalControl.touched)
        ">
      </oiq-form-input>
    }
  `,
})
export class CollectionFilterIdsComponent extends CollectionTableFilterBaseComponent {
  internalControl = new FormControl<any[]>([]);

  idsDefinition = computed(() => {
    const _definition = this.definition();
    if (!_definition) {
      return null;
    }
    return { ..._definition, list: { ..._definition.ids, multi: true } };
  });

  constructor() {
    super();
    this.internalControl.setValidators([this._validateIds.bind(this)]);
    effect(() => {
      const _value = this.value();
      const _definition = this.idsDefinition();
      if (!_definition) {
        return;
      }
      const _normalized = CollectionFilterFromQueryList(_value, _definition, false) as any;
      this.internalControl.setValue(_normalized);
    });
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    const _normalizedDefinition = { ...definition, list: { ...definition.ids } };
    return CollectionFilterFromQueryList(query, _normalizedDefinition, true) as {
      [key: string]: any;
    };
  }

  getQuery() {
    const _definition = this.idsDefinition();

    const _value: any[] = Array.isArray(this.internalControl.value)
      ? (this.internalControl.value as any[])
      : ((this.internalControl.value as any) || '').toString().split(',');
    const _list = _value.map(v => v.trim());
    return CollectionFilterFormatPayloadList(_list, _definition);
  }

  onValueChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'blur':
      case 'enter':
        if (this.internalControl.invalid) {
          return;
        }
        this.handleValueChanged();
        break;
      case 'clear':
        this.internalControl.setValue(null);
        this.handleValueChanged();
        break;
    }
  }

  private _validateIds(control: AbstractControl): ValidationErrors | null {
    const _value: any[] = Array.isArray(control.value)
      ? (control.value as any[])
      : (control.value || '').toString().split(',');

    if (!control.value || !_value || _value.length === 0) {
      return null;
    }

    const _ids = _value.map(v => v.trim());
    const _emptyIds = _ids.filter(v => v.length === 0);
    if (_emptyIds.length > 0) {
      return { emptyIds: true };
    }

    const _invalidIds = _ids.filter(v => !v.match(/^[0-9]+$/));
    if (_invalidIds.length > 0) {
      return { invalidIds: true };
    }

    return null;
  }
}
